import type { DeviceProfile } from "~/types/midi";

/**
 * Default device profile for YO! Monosynth
 *
 * This profile enables scrambling for all mapped CCs with safe min/max ranges
 * to prevent issues like inaudible volume or extreme resonance.
 *
 * Based on the Moog Subsequent 37-style CC mapping documented in:
 * docs/SYNTH_CC_MAPPING.md
 */
export const YO_TEST_SYNTH_DEFAULT_PROFILE: DeviceProfile = {
  id: "yo-test-synth-default",
  name: "Default",
  deviceName: "YO! Monosynth",
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    // === STANDARD MIDI CCs ===
    {
      number: 1,
      enabled: false, // Disabled by default - can be disruptive
      min: 0,
      max: 127,
      label: "Modulation",
    },
    {
      number: 7,
      enabled: false, // Disabled - dangerous if too low
      min: 80,  // ~-10dB - loud and clear
      max: 110, // ~-2dB - very loud but not clipping
      label: "Volume",
    },
    {
      number: 11,
      enabled: false, // Disabled - same as volume
      min: 80,  // ~-10dB - loud and clear
      max: 110, // ~-2dB - very loud but not clipping
      label: "Expression",
    },
    {
      number: 74,
      enabled: true, // ✅ Great for sound design
      min: 50,   // ~1.5kHz - prevents overly filtered/muffled sounds
      max: 110,  // ~15kHz - avoid extreme highs
      label: "Filter Cutoff",
    },
    {
      number: 71,
      enabled: true, // ✅ Adds character
      min: 0,
      max: 80,  // Avoid self-oscillation
      label: "Filter Resonance",
    },

    // === AMP ENVELOPE ===
    {
      number: 73,
      enabled: false, // Disabled - too short causes clicks, keep preset value
      min: 3,    // ~0.01s - avoid clicks from instant attack
      max: 80,   // ~1.0s - avoid extremely slow attacks that seem quiet
      label: "Amp Attack",
    },
    {
      number: 75,
      enabled: true, // ✅ Decay - safe to scramble
      min: 10,   // Avoid instant decay
      max: 127,
      label: "Amp Decay",
    },
    {
      number: 70,
      enabled: true, // ✅ Sustain - safe to scramble
      min: 0,
      max: 127,
      label: "Amp Sustain",
    },
    {
      number: 72,
      enabled: false, // Disabled - too short causes abrupt cutoff, keep preset value
      min: 10,   // Avoid clicks
      max: 127,
      label: "Amp Release",
    },

    // === FILTER ENVELOPE ===
    {
      number: 16,
      enabled: false, // Disabled - filter env attack kept stable for consistent response
      min: 0,
      max: 127,
      label: "Filter Env Attack",
    },
    {
      number: 17,
      enabled: true, // ✅ Filter Env Decay - safe to scramble
      min: 0,
      max: 127,
      label: "Filter Env Decay",
    },
    {
      number: 18,
      enabled: true, // ✅ Filter Env Sustain - safe to scramble
      min: 0,
      max: 127,
      label: "Filter Env Sustain",
    },
    {
      number: 19,
      enabled: true, // ✅ Filter Env Release - safe to scramble (less critical than amp release)
      min: 0,
      max: 127,
      label: "Filter Env Release",
    },
    {
      number: 20,
      enabled: true, // ✅ Filter Env Amount
      min: 0,
      max: 70,   // ~3.8 octaves - avoid extreme filter sweeps
      label: "Filter Env Amount",
    },
    {
      number: 21,
      enabled: true, // ✅ Filter Env Base Freq
      min: 20,   // ~200Hz - avoid extremely low base frequencies
      max: 100,  // ~1.2kHz - prevent overly bright base
      label: "Filter Env Base",
    },

    // === OSCILLATOR ===
    {
      number: 76,
      enabled: true, // ✅ Waveform changes
      min: 0,
      max: 127,
      label: "Oscillator Type",
    },
    {
      number: 77,
      enabled: false, // Disabled - can make it out of tune
      min: 0,
      max: 127,
      label: "Oscillator Detune",
    },
    {
      number: 78,
      enabled: false, // Disabled - can be too glitchy
      min: 0,
      max: 127,
      label: "Portamento",
    },
  ],
};

/**
 * Get the default profile for YO! Monosynth
 * Returns a fresh copy to avoid mutation
 */
export function getYoTestSynthDefaultProfile(): DeviceProfile {
  return {
    ...YO_TEST_SYNTH_DEFAULT_PROFILE,
    ccConfig: YO_TEST_SYNTH_DEFAULT_PROFILE.ccConfig.map(cc => ({ ...cc })),
  };
}
